import React, { useState } from "react";
import { MdClose } from "react-icons/md";
import { Card, FeeEditor, NumberInput, Row, Toggle, inputCls, rupees, setIn } from "./controls";

// Exemptions, Collection, Barbera credits and Customer wording tabs.

const Exempt = ({ policy, onChange, k, label, hint, children }) => {
  const e = policy.exemptions[k];
  return (
    <Row label={label} hint={hint}>
      {children && <span className={e.enabled ? "" : "opacity-50 pointer-events-none"}>{children}</span>}
      <Toggle checked={e.enabled} onChange={(v) => onChange(setIn(policy, `exemptions.${k}.enabled`, v))} />
    </Row>
  );
};

export const PolicyExemptions = ({ policy, onChange }) => {
  const ex = policy.exemptions;
  const set = (path) => (v) => onChange(setIn(policy, `exemptions.${path}`, v));
  const [day, setDay] = useState("");
  const dates = ex.disruptionDays.dates || [];
  const capTypes = ex.monthlyCap.bookingTypes || ["STANDARD", "PACKAGE"];

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-gray-300">A fee is waived when any of these is true. They're checked top to bottom and the first match is recorded as the reason.</p>
      <Card>
        <Exempt policy={policy} onChange={onChange} k="disruptionDays" label="Disruption days" hint="Rain, bandh, festival - every fee for a slot on these dates is waived.">
          <span className="flex flex-wrap items-center gap-1.5">
            {dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs text-gray-700">
                {d}
                <button type="button" title="Remove" onClick={() => set("disruptionDays.dates")(dates.filter((x) => x !== d))}><MdClose /></button>
              </span>
            ))}
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className={inputCls} />
            <button
              type="button"
              disabled={!day || dates.includes(day)}
              onClick={() => { set("disruptionDays.dates")([...dates, day].sort()); setDay(""); }}
              className="px-2 py-1 rounded-lg bg-gray-900 text-white text-xs font-semibold disabled:opacity-40"
            >
              Add
            </button>
          </span>
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="noBeauticianAssigned" label="No beautician assigned yet" />
        <Exempt policy={policy} onChange={onChange} k="beauticianLate" label="Beautician running late" hint="Arrived (or hadn't arrived) this long after the slot.">
          <NumberInput value={ex.beauticianLate.minutes} suffix="min or more" width="w-16" onChange={set("beauticianLate.minutes")} />
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="barberaChangedBooking" label="Barbera changed the booking" hint="Reassigned the beautician, changed the slot or the price, shortly before.">
          <NumberInput value={ex.barberaChangedBooking.withinHours} prefix="within" suffix="hours" width="w-16" step={0.5} onChange={set("barberaChangedBooking.withinHours")} />
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="paymentTrouble" label="Payment trouble" hint="An online payment failed shortly before they cancelled.">
          <NumberInput value={ex.paymentTrouble.minutes} prefix="within" suffix="min" width="w-16" onChange={set("paymentTrouble.minutes")} />
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="graceAfterBooking" label="Grace period after booking" hint="Cancelling right after booking (mistakes, wrong slot).">
          <NumberInput value={ex.graceAfterBooking.minutes} prefix="within" suffix="min" width="w-16" onChange={set("graceAfterBooking.minutes")} />
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="newCustomerFirstBooking" label="New customer's first booking" />
        <Exempt policy={policy} onChange={onChange} k="loyalCustomer" label="Loyal customers" hint="Completed bookings so far.">
          <NumberInput value={ex.loyalCustomer.minCompletedBookings} suffix="or more" width="w-16" onChange={set("loyalCustomer.minCompletedBookings")} />
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="firstLateCancelsFree" label="First late cancellations free" hint="Late cancellations (not no-shows) a customer gets free.">
          <NumberInput value={ex.firstLateCancelsFree.count} width="w-14" onChange={set("firstLateCancelsFree.count")} />
          <select
            value={ex.firstLateCancelsFree.perDays ? "PERIOD" : "LIFETIME"}
            onChange={(e) => set("firstLateCancelsFree.perDays")(e.target.value === "LIFETIME" ? null : 90)}
            className={inputCls}
          >
            <option value="LIFETIME">ever</option>
            <option value="PERIOD">per …</option>
          </select>
          {ex.firstLateCancelsFree.perDays !== null && ex.firstLateCancelsFree.perDays !== undefined && (
            <NumberInput value={ex.firstLateCancelsFree.perDays} suffix="days" width="w-16" onChange={set("firstLateCancelsFree.perDays")} />
          )}
        </Exempt>
        <Exempt policy={policy} onChange={onChange} k="monthlyCap" label="Monthly cap per customer" hint="Most a customer pays in fees in a calendar month.">
          <NumberInput prefix="₹" value={ex.monthlyCap.amount} onChange={set("monthlyCap.amount")} />
          {["STANDARD", "BRIDAL", "PACKAGE"].map((t) => (
            <label key={t} className="text-xs text-gray-600 inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={capTypes.includes(t)}
                onChange={(e) => set("monthlyCap.bookingTypes")(e.target.checked ? [...capTypes, t] : capTypes.filter((x) => x !== t))}
              />
              {t.charAt(0) + t.slice(1).toLowerCase()}
            </label>
          ))}
        </Exempt>
      </Card>
      <p className="text-xs text-gray-400">Support can also waive any single fee from the Fees &amp; credits tab or the booking's details.</p>
    </div>
  );
};

export const PolicyCollection = ({ policy, onChange }) => {
  const c = policy.collection;
  const set = (path) => (v) => onChange(setIn(policy, `collection.${path}`, v));
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        These apply in <b>Live</b>, and only to bookings whose customer accepted the policy at checkout. In Shadow nothing is collected - the record only shows what would have been charged.
      </div>
      <Card title="How a fee is paid" hint="Tried in this order until the fee is covered. The wallet is never used - it holds promotional / referral credit (max ₹20 a booking), not the customer's money.">
        <Row label="1. Keep it from the Razorpay refund" hint="Prepaid bookings: the rest is refunded. Promo credit used on the booking always goes back to the wallet.">
          <Toggle checked={c.deductFromRefund} onChange={set("deductFromRefund")} />
        </Row>
        <Row label="2. Keep the rest as dues" hint="Customer pays them with “Pay now” in the app, or agrees at checkout to add them to a pay-after-service booking. Off = write the rest off.">
          <span className={c.pendingDues.enabled ? "" : "opacity-50 pointer-events-none"}>
            <NumberInput prefix="write off after" value={c.pendingDues.writeOffAfterDays} suffix="days" width="w-16" onChange={set("pendingDues.writeOffAfterDays")} />
          </span>
          <Toggle checked={c.pendingDues.enabled} onChange={set("pendingDues.enabled")} />
        </Row>
        <Row label="Also SMS a payment link for dues" hint="Razorpay texts the customer a link right away. The app always offers “Pay now” for dues.">
          <Toggle checked={c.paymentLink.enabled} onChange={set("paymentLink.enabled")} />
        </Row>
      </Card>
      <Card title="Ask for online payment upfront" hint="So a later fee can simply be kept from the refund. Customers are never blocked from booking.">
        <Row label="Repeat cancellers" hint="After this many late cancellations or no-shows…">
          <span className={c.prepayRepeatCancellers.enabled ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2 opacity-50 pointer-events-none"}>
            <NumberInput value={c.prepayRepeatCancellers.lateCancels} width="w-14" onChange={set("prepayRepeatCancellers.lateCancels")} />
            <NumberInput prefix="in" value={c.prepayRepeatCancellers.inDays} suffix="days, pay online for" width="w-16" onChange={set("prepayRepeatCancellers.inDays")} />
            <NumberInput value={c.prepayRepeatCancellers.prepayForDays} suffix="days" width="w-16" onChange={set("prepayRepeatCancellers.prepayForDays")} />
          </span>
          <Toggle checked={c.prepayRepeatCancellers.enabled} onChange={set("prepayRepeatCancellers.enabled")} />
        </Row>
        <Row label="Risky bookings" hint="Any one of these must be paid online in full (pay-after-service is hidden).">
          <span className={c.prepayRiskyBookings.enabled ? "flex flex-wrap items-center gap-3" : "flex flex-wrap items-center gap-3 opacity-50 pointer-events-none"}>
            <NumberInput prefix="above ₹" value={c.prepayRiskyBookings.aboveAmount} width="w-24" onChange={set("prepayRiskyBookings.aboveAmount")} />
            <label className="text-xs text-gray-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={c.prepayRiskyBookings.bridal} onChange={(e) => set("prepayRiskyBookings.bridal")(e.target.checked)} /> Bridal
            </label>
            <label className="text-xs text-gray-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={c.prepayRiskyBookings.sameDaySlots} onChange={(e) => set("prepayRiskyBookings.sameDaySlots")(e.target.checked)} /> Same-day slots
            </label>
          </span>
          <Toggle checked={c.prepayRiskyBookings.enabled} onChange={set("prepayRiskyBookings.enabled")} />
        </Row>
      </Card>
    </div>
  );
};

export const PolicyCompensation = ({ policy, onChange }) => {
  const c = policy.compensation;
  const set = (path) => (v) => onChange(setIn(policy, `compensation.${path}`, v));
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
        India's Consumer Protection (E-Commerce) Rules 2020 allow cancellation fees only if the platform bears similar charges when it cancels. These credits are that side. They are off for now; note they are paid into the wallet, where customers can only use ₹20 a booking.
      </div>
      <Card title="Wallet credit to the customer when…">
        <Row label="Barbera cancels late" hint="Declined as Admin/Ops or Employee fault.">
          <span className={c.barberaCancel.enabled ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2 opacity-50 pointer-events-none"}>
            <NumberInput prefix="within" value={c.barberaCancel.withinHours} suffix="hours of the slot →" width="w-16" step={0.5} onChange={set("barberaCancel.withinHours")} />
            <FeeEditor fee={c.barberaCancel.credit} onChange={set("barberaCancel.credit")} />
          </span>
          <Toggle checked={c.barberaCancel.enabled} onChange={set("barberaCancel.enabled")} />
        </Row>
        <Row label="Beautician doesn't turn up" hint="Declined as Employee → Running late / no-show.">
          <FeeEditor fee={c.beauticianNoShow.credit} disabled={!c.beauticianNoShow.enabled} onChange={set("beauticianNoShow.credit")} />
          <Toggle checked={c.beauticianNoShow.enabled} onChange={set("beauticianNoShow.enabled")} />
        </Row>
        <Row label="Beautician late and the customer cancels" hint="Arrival this long after the slot.">
          <span className={c.beauticianLate.enabled ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2 opacity-50 pointer-events-none"}>
            <NumberInput value={c.beauticianLate.minutes} suffix="min or more →" width="w-16" onChange={set("beauticianLate.minutes")} />
            <FeeEditor fee={c.beauticianLate.credit} onChange={set("beauticianLate.credit")} />
          </span>
          <Toggle checked={c.beauticianLate.enabled} onChange={set("beauticianLate.enabled")} />
        </Row>
        <Row label="Credit expires after">
          <NumberInput value={c.creditExpiryDays} suffix="days" width="w-16" onChange={set("creditExpiryDays")} />
        </Row>
      </Card>
    </div>
  );
};

const TEXTS = [
  { key: "checkout", label: "Checkout (next to the tick-box)" },
  { key: "bookingCard", label: "Booking card" },
  { key: "cancelSheetFee", label: "Cancel sheet - when a fee applies" },
  { key: "cancelSheetFree", label: "Cancel sheet - when it's free" },
  { key: "pendingDues", label: "Bill line for an unpaid fee" },
];
const SAMPLE = { "{fee}": rupees(99), "{time}": "6:30 AM", "{amount}": rupees(99) };
const fill = (text) => Object.entries(SAMPLE).reduce((s, [k, v]) => s.split(k).join(v), text || "");

export const PolicyWording = ({ policy, onChange }) => (
  <div className="space-y-4 max-w-4xl">
    <p className="text-sm text-gray-300">
      What customers read in the app once the policy is Live. <code className="text-[#FBB033]">{"{fee}"}</code> is the fee,
      <code className="text-[#FBB033]"> {"{time}"}</code> the end of free cancellation, <code className="text-[#FBB033]">{"{amount}"}</code> an unpaid fee.
    </p>
    <Card>
      {TEXTS.map((t) => (
        <div key={t.key} className="py-3 border-t border-gray-100 first:border-t-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.label}</p>
          <textarea
            rows={2}
            value={policy.customerText[t.key] || ""}
            onChange={(e) => onChange(setIn(policy, `customerText.${t.key}`, e.target.value))}
            className={`${inputCls} w-full`}
          />
          <p className="text-xs text-gray-500 mt-1">Customer sees: <span className="text-gray-800">{fill(policy.customerText[t.key])}</span></p>
        </div>
      ))}
    </Card>
  </div>
);
